import type { LibraryEntry, StorageAdapter } from '@bw/document';

const AUTOSAVE_FILE = 'autosave.bwall';
const DB_NAME = 'brickwall';
const DB_VERSION = 1;
const STORE = 'projects';

/** True when this browser can save in place rather than only download. */
export const canSaveInPlace = (): boolean =>
  typeof window !== 'undefined' && 'showSaveFilePicker' in window;

async function opfsRoot(): Promise<FileSystemDirectoryHandle | null> {
  try {
    if (typeof navigator === 'undefined' || navigator.storage?.getDirectory === undefined) return null;
    return await navigator.storage.getDirectory();
  } catch {
    return null;
  }
}

function openDatabase(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
  });
}

interface LibraryRow extends LibraryEntry {
  readonly text: string;
}

/**
 * Design document D4 — OPFS for crash recovery, IndexedDB for the project
 * library. Both degrade to no-ops rather than throwing, so a browser with
 * storage disabled still runs the editor.
 */
export class BrowserStorage implements StorageAdapter {
  async readAutosave(): Promise<string | null> {
    const root = await opfsRoot();
    if (root === null) return null;
    try {
      const handle = await root.getFileHandle(AUTOSAVE_FILE);
      const file = await handle.getFile();
      const text = await file.text();
      return text.length > 0 ? text : null;
    } catch {
      return null;
    }
  }

  async writeAutosave(text: string): Promise<void> {
    const root = await opfsRoot();
    if (root === null) return;
    try {
      const handle = await root.getFileHandle(AUTOSAVE_FILE, { create: true });
      const writable = await handle.createWritable();
      await writable.write(text);
      await writable.close();
    } catch {
      // Storage may be denied; autosave is a convenience, never a guarantee.
    }
  }

  async clearAutosave(): Promise<void> {
    const root = await opfsRoot();
    if (root === null) return;
    try {
      await root.removeEntry(AUTOSAVE_FILE);
    } catch {
      // already gone
    }
  }

  async #withStore<T>(
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => IDBRequest,
    fallback: T,
  ): Promise<T> {
    const db = await openDatabase();
    if (db === null) return fallback;
    return new Promise<T>((resolve) => {
      const transaction = db.transaction(STORE, mode);
      const request = run(transaction.objectStore(STORE));
      request.onsuccess = () => resolve(request.result as T);
      request.onerror = () => resolve(fallback);
      transaction.oncomplete = () => db.close();
    });
  }

  async listLibrary(): Promise<LibraryEntry[]> {
    const rows = await this.#withStore<LibraryRow[]>('readonly', (store) => store.getAll(), []);
    return rows
      .map(({ id, name, savedAt, brickCount }) => ({ id, name, savedAt, brickCount }))
      .sort((a, b) => b.savedAt.localeCompare(a.savedAt));
  }

  async putLibrary(entry: LibraryEntry, text: string): Promise<void> {
    await this.#withStore('readwrite', (store) => store.put({ ...entry, text }), undefined);
  }

  async getLibrary(id: string): Promise<string | null> {
    const row = await this.#withStore<LibraryRow | undefined>(
      'readonly',
      (store) => store.get(id),
      undefined,
    );
    return row?.text ?? null;
  }

  async deleteLibrary(id: string): Promise<void> {
    await this.#withStore('readwrite', (store) => store.delete(id), undefined);
  }
}

export interface OpenedFile {
  readonly text: string;
  readonly name: string;
  readonly handle: FileSystemFileHandle | null;
}

/** Open a project, through the File System Access API where it exists. */
export async function openProjectFile(): Promise<OpenedFile | null> {
  return pickFile('Brickwall projects', ['.bwall', '.json']);
}

/**
 * Open one wall from the desktop tool.
 *
 * Its own picker rather than a reuse of the project one, so the file list is
 * filtered to what this action can actually take: a `.bwall` offered here
 * would be refused a moment later for being a whole project.
 */
export async function openWallFile(): Promise<OpenedFile | null> {
  return pickFile('Brickwall Designer walls', ['.json']);
}

async function pickFile(
  description: string,
  extensions: readonly string[],
): Promise<OpenedFile | null> {
  if ('showOpenFilePicker' in window) {
    try {
      const [handle] = await (window as unknown as {
        showOpenFilePicker: (options: unknown) => Promise<FileSystemFileHandle[]>;
      }).showOpenFilePicker({
        types: [{ description, accept: { 'application/json': [...extensions] } }],
      });
      if (handle === undefined) return null;
      const file = await handle.getFile();
      return { text: await file.text(), name: file.name, handle };
    } catch {
      return null; // the user dismissed the picker
    }
  }
  return new Promise<OpenedFile | null>((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = extensions.join(',');
    input.onchange = () => {
      const file = input.files?.[0];
      if (file === undefined) {
        resolve(null);
        return;
      }
      void file.text().then((text) => resolve({ text, name: file.name, handle: null }));
    };
    input.oncancel = () => resolve(null);
    input.click();
  });
}

/** Write to a retained handle, or fall back to a download. */
export async function writeProjectFile(
  handle: FileSystemFileHandle | null,
  suggestedName: string,
  text: string,
): Promise<{ handle: FileSystemFileHandle | null; name: string } | null> {
  if (handle !== null) {
    const writable = await handle.createWritable();
    await writable.write(text);
    await writable.close();
    return { handle, name: handle.name };
  }
  if ('showSaveFilePicker' in window) {
    try {
      const created = await (window as unknown as {
        showSaveFilePicker: (options: unknown) => Promise<FileSystemFileHandle>;
      }).showSaveFilePicker({
        suggestedName,
        types: [{ description: 'Brickwall project', accept: { 'application/json': ['.bwall'] } }],
      });
      const writable = await created.createWritable();
      await writable.write(text);
      await writable.close();
      return { handle: created, name: created.name };
    } catch {
      return null;
    }
  }
  downloadText(suggestedName, text);
  return { handle: null, name: suggestedName };
}

export function downloadText(name: string, text: string): void {
  const blob = new Blob([text], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
